export const ROUTE_LOGIN = '/login';
export const ROUTE_HOME = '/';
export const ROUTE_DASHBOARDS = '/dashboards';
export const ROUTE_USERS = '/users';
export const ROUTE_DEPARTMENTS = '/departments';
export const ROUTE_VEHICLES = '/vehicles';
export const ROUTE_VEHICLE_GROUPS = '/vehicle-groups';
export const ROUTE_DRIVERS = '/drivers';
export const ROUTE_APPLICATIONS = '/applications';
export const ROUTE_APPLICATIONS_DISPATCH_POOL = '/applications/dispatch-pool';
export const ROUTE_DISPATCHES = '/dispatches';
export const ROUTE_OPERATION_LOGS = '/operation-logs';
export const ROUTE_LOGIN_LOGS = '/login-logs';
export const ROUTE_ACCOUNT = '/account';
export const ROUTE_FLOW_DEFINITIONS = '/flow/definitions';
export const ROUTE_MAINTENANCE_ORDERS = '/maintenance-orders';
export const ROUTE_MAINTENANCE_RULES = '/maintenance-rules';
export const ROUTE_DICTIONARIES = '/dictionaries';
export const ROUTE_REMINDERS = '/reminders';
export const ROUTE_DRIVER_SCHEDULES = '/driver-schedules';
export const ROUTE_SHIFT_TEMPLATES = '/shift-templates';
export const ROUTE_PERFORMANCE_INDICATORS = '/performance-indicators';
export const ROUTE_PERFORMANCE_RECORDS = '/performance-records';
export const ROUTE_REWARD_PENALTIES = '/reward-penalties';
export const ROUTE_NOTIFICATION_LOGS = '/notification-logs';
export const ROUTE_NOTIFICATION_CHANNELS = '/notification-channels';
export const ROUTE_NOTIFICATION_INBOX = '/messages';
export const ROUTE_NOTIFICATION_BLACKLIST = '/notification-blacklist';
export const ROUTE_NOTIFICATION_PREFERENCE = '/my-notification-preference';
export const ROUTE_NOTIFICATION_EVENTS = '/notification-events';
export const ROUTE_NOTIFICATION_TEMPLATES = '/notification-templates';
export const ROUTE_NOTIFICATION_TEMPLATE_NEW = '/notification-templates/new';
export const templateEditPath = (id: number | string) => `/notification-templates/${id}`;
export const ROUTE_403 = '/403';
export const ROUTE_404 = '/404';

/** 用户状态值（与后端 PRD-01 §6.2 保持一致） */
export const USER_STATUS = {
  PENDING: 1,
  ACTIVE: 2,
  LOCKED: 3,
  DISABLED: 4,
} as const;

export const USER_STATUS_OPTIONS = [
  { value: USER_STATUS.PENDING, label: '待激活' },
  { value: USER_STATUS.ACTIVE, label: '正常' },
  { value: USER_STATUS.LOCKED, label: '锁定' },
  { value: USER_STATUS.DISABLED, label: '停用' },
] as const;

/** 颜色映射 */
export const USER_STATUS_COLOR: Record<number, string> = {
  1: 'default',
  2: 'success',
  3: 'warning',
  4: 'error',
};

/** 部门状态值（与后端 PRD-02 §5.1 保持一致） */
export const DEPARTMENT_STATUS = {
  ACTIVE: 1,
  DISABLED: 2,
} as const;

export const DEPARTMENT_STATUS_OPTIONS = [
  { value: DEPARTMENT_STATUS.ACTIVE, label: '启用' },
  { value: DEPARTMENT_STATUS.DISABLED, label: '停用' },
] as const;

export const DEPARTMENT_STATUS_COLOR: Record<number, string> = {
  1: 'success',
  2: 'default',
};

/** 部门最大层级（决策 D-41） */
export const DEPARTMENT_MAX_LEVEL = 5;

// ============================================================
// 车辆（PRD-03，与 backend VehicleService / Resource 对齐）
// ============================================================

/** 车辆状态值（PRD-03 §4.2） */
export const VEHICLE_STATUS = {
  AVAILABLE: 1,
  DISPATCHED: 2,
  RUNNING: 3,
  MAINTAINING: 4,
  SERVICING: 5,
  DISABLED: 6,
  SCRAPPED: 7,
} as const;

export const VEHICLE_STATUS_OPTIONS = [
  { value: VEHICLE_STATUS.AVAILABLE, label: '可用' },
  { value: VEHICLE_STATUS.DISPATCHED, label: '已派车' },
  { value: VEHICLE_STATUS.RUNNING, label: '行驶中' },
  { value: VEHICLE_STATUS.MAINTAINING, label: '维修中' },
  { value: VEHICLE_STATUS.SERVICING, label: '保养中' },
  { value: VEHICLE_STATUS.DISABLED, label: '停用' },
  { value: VEHICLE_STATUS.SCRAPPED, label: '报废' },
] as const;

/** 状态 → Antd Tag 色（绿=可用，蓝=已派车，紫=行驶中，黄=维修，橙=保养，灰=停用，红=报废） */
export const VEHICLE_STATUS_COLOR: Record<number, string> = {
  1: 'success',
  2: 'processing',
  3: 'cyan',
  4: 'warning',
  5: 'gold',
  6: 'default',
  7: 'error',
};

/**
 * 人工状态流转矩阵（镜像 backend VehicleService::HUMAN_TRANSITIONS）
 * 源状态 → 允许目标状态集合
 * 缺失的 key 表示该状态不可由人工切换（2/3 由派车流程接管；7 终态）
 */
export const HUMAN_STATUS_TRANSITIONS: Record<number, number[]> = {
  1: [4, 5, 6, 7],
  4: [1, 5, 7],
  5: [1, 4, 7],
  6: [1, 7],
};

/** 车辆类型（D-48 一期 7 类硬编码） */
export const VEHICLE_TYPE = {
  SEDAN: 1,
  BUSINESS: 2,
  SUV: 3,
  VAN: 4,
  TRUCK: 5,
  BUS: 6,
  NEW_ENERGY: 7,
} as const;

export const VEHICLE_TYPE_OPTIONS = [
  { value: VEHICLE_TYPE.SEDAN, label: '轿车' },
  { value: VEHICLE_TYPE.BUSINESS, label: '商务车' },
  { value: VEHICLE_TYPE.SUV, label: 'SUV' },
  { value: VEHICLE_TYPE.VAN, label: '面包车' },
  { value: VEHICLE_TYPE.TRUCK, label: '货车' },
  { value: VEHICLE_TYPE.BUS, label: '客车' },
  { value: VEHICLE_TYPE.NEW_ENERGY, label: '新能源' },
] as const;

/** 燃油类型（D-49 一期 5 类硬编码） */
export const FUEL_TYPE = {
  GASOLINE: 1,
  DIESEL: 2,
  ELECTRIC: 3,
  HYBRID: 4,
  HYDROGEN: 5,
} as const;

export const FUEL_TYPE_OPTIONS = [
  { value: FUEL_TYPE.GASOLINE, label: '汽油' },
  { value: FUEL_TYPE.DIESEL, label: '柴油' },
  { value: FUEL_TYPE.ELECTRIC, label: '电' },
  { value: FUEL_TYPE.HYBRID, label: '混动' },
  { value: FUEL_TYPE.HYDROGEN, label: '氢' },
] as const;

/** 车辆附件类型（PRD-03 §5.4 doc_type） */
export const VEHICLE_DOC_TYPE = {
  DRIVING_LICENSE: 1,
  INSURANCE: 2,
  INSPECTION: 3,
  PURCHASE_TAX: 4,
  REGISTRATION: 5,
  OTHER: 99,
} as const;

export const VEHICLE_DOC_TYPE_OPTIONS = [
  { value: VEHICLE_DOC_TYPE.DRIVING_LICENSE, label: '行驶证' },
  { value: VEHICLE_DOC_TYPE.INSURANCE, label: '保险单' },
  { value: VEHICLE_DOC_TYPE.INSPECTION, label: '年检证明' },
  { value: VEHICLE_DOC_TYPE.PURCHASE_TAX, label: '购置税' },
  { value: VEHICLE_DOC_TYPE.REGISTRATION, label: '登记证' },
  { value: VEHICLE_DOC_TYPE.OTHER, label: '其他' },
] as const;

/** 附件大小上限（D-53，10MB） */
export const VEHICLE_DOC_MAX_SIZE = 10 * 1024 * 1024;

/** 附件允许的 MIME（D-53） */
export const VEHICLE_DOC_ALLOWED_MIMES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
] as const;

/** 附件允许的扩展名（用于 input accept） */
export const VEHICLE_DOC_ALLOWED_EXT = '.pdf,.jpg,.jpeg,.png';

/** 座位数上限（D-50） */
export const VEHICLE_SEATS_MAX = 60;

// ============================================================
// 司机（PRD-04，与 backend DriverService 对齐）
// ============================================================

/** 司机状态值（PRD-04 §4.2） */
export const DRIVER_STATUS = {
  AVAILABLE: 1,
  DISPATCHED: 2,
  DRIVING: 3,
  VACATION: 4,
  LEAVE: 5,
  SUSPENDED: 6,
  RESIGNED: 7,
} as const;

export const DRIVER_STATUS_OPTIONS = [
  { value: DRIVER_STATUS.AVAILABLE, label: '可用' },
  { value: DRIVER_STATUS.DISPATCHED, label: '已派车' },
  { value: DRIVER_STATUS.DRIVING, label: '行驶中' },
  { value: DRIVER_STATUS.VACATION, label: '休假' },
  { value: DRIVER_STATUS.LEAVE, label: '请假' },
  { value: DRIVER_STATUS.SUSPENDED, label: '暂停派车' },
  { value: DRIVER_STATUS.RESIGNED, label: '离职' },
] as const;

/** 状态 → Antd Tag 色（绿=可用，processing=已派车，cyan=行驶中，黄=休假，gold=请假，灰=暂停，红=离职） */
export const DRIVER_STATUS_COLOR: Record<number, string> = {
  1: 'success',
  2: 'processing',
  3: 'cyan',
  4: 'warning',
  5: 'gold',
  6: 'default',
  7: 'error',
};

/**
 * 司机人工状态流转矩阵（镜像 backend DriverService::HUMAN_TRANSITIONS）
 * 缺失的 key 表示该状态不可由人工切换（2/3 由派车流程接管；7 终态）
 */
export const HUMAN_DRIVER_TRANSITIONS: Record<number, number[]> = {
  1: [4, 5, 6, 7],
  4: [1, 6, 7],
  5: [1, 6, 7],
  6: [1, 7],
};

/** 准驾车型字典（D-67） */
export const LICENSE_CLASS_OPTIONS = [
  { value: 'A1', label: 'A1（大型客车）' },
  { value: 'A2', label: 'A2（牵引车）' },
  { value: 'A3', label: 'A3（城市公交）' },
  { value: 'B1', label: 'B1（中型客车）' },
  { value: 'B2', label: 'B2（大型货车）' },
  { value: 'C1', label: 'C1（小型汽车）' },
  { value: 'C2', label: 'C2（小型自动挡）' },
  { value: 'C3', label: 'C3（低速载货）' },
  { value: 'C4', label: 'C4（三轮汽车）' },
  { value: 'C5', label: 'C5（残疾人专用）' },
  { value: 'D', label: 'D（摩托车）' },
  { value: 'E', label: 'E（轻便摩托）' },
  { value: 'F', label: 'F（轻便摩托）' },
] as const;

/** 资质类型（PRD-04 §5.2） */
export const DRIVER_QUAL_TYPE = {
  LICENSE: 1,
  QUALIFICATION: 2,
  NO_ACCIDENT: 3,
  OTHER: 99,
} as const;

export const DRIVER_QUAL_TYPE_OPTIONS = [
  { value: DRIVER_QUAL_TYPE.LICENSE, label: '驾驶证' },
  { value: DRIVER_QUAL_TYPE.QUALIFICATION, label: '从业资格证' },
  { value: DRIVER_QUAL_TYPE.NO_ACCIDENT, label: '无重大事故证明' },
  { value: DRIVER_QUAL_TYPE.OTHER, label: '其他' },
] as const;

/** 驾照扣分阈值（D-63） */
export const DRIVER_POINTS_MAX = 24;
export const DRIVER_POINTS_WARN_THRESHOLD = 12;

// ============================================================
// 用车申请（PRD-05，与 backend ApplicationService / ApplicationResource 对齐）
// ============================================================

/** 申请状态（PRD-05 §4.2，12 个值） */
export const APPLICATION_STATUS = {
  DRAFT: 1,
  PENDING: 2,
  RETURNED: 3,
  APPROVED: 4,
  REJECTED: 5,
  CANCELLED: 6,
  CANCEL_PENDING: 7,
  DISPATCHED: 8,
  RUNNING: 9,
  COMPLETED: 10,
  CANCELED_DISPATCH: 11,
  ABNORMAL: 12,
} as const;

export const APPLICATION_STATUS_OPTIONS = [
  { value: APPLICATION_STATUS.DRAFT, label: '草稿' },
  { value: APPLICATION_STATUS.PENDING, label: '待审批' },
  { value: APPLICATION_STATUS.RETURNED, label: '退回修改' },
  { value: APPLICATION_STATUS.APPROVED, label: '已审批' },
  { value: APPLICATION_STATUS.REJECTED, label: '已驳回' },
  { value: APPLICATION_STATUS.CANCELLED, label: '已撤销' },
  { value: APPLICATION_STATUS.CANCEL_PENDING, label: '撤销待确认' },
  { value: APPLICATION_STATUS.DISPATCHED, label: '已派车' },
  { value: APPLICATION_STATUS.RUNNING, label: '行驶中' },
  { value: APPLICATION_STATUS.COMPLETED, label: '已完成' },
  { value: APPLICATION_STATUS.CANCELED_DISPATCH, label: '已取消' },
  { value: APPLICATION_STATUS.ABNORMAL, label: '异常终止' },
] as const;

/** 颜色映射（草稿灰/待审批蓝/退回橙/已审批绿/驳回红/撤销灰/撤销待确认橙/派车青/行驶蓝/完成绿/取消灰/异常红） */
export const APPLICATION_STATUS_COLOR: Record<number, string> = {
  1: 'default',
  2: 'processing',
  3: 'warning',
  4: 'success',
  5: 'error',
  6: 'default',
  7: 'warning',
  8: 'cyan',
  9: 'blue',
  10: 'success',
  11: 'default',
  12: 'error',
};

/** 历史动作 label 映射（与 ApplicationHistoryResource::actionLabel() 对齐） */
export const APPLICATION_HISTORY_ACTION_LABEL: Record<string, string> = {
  draft_save: '保存草稿',
  submit: '提交',
  update: '修改',
  approve: '审批通过',
  reject: '驳回',
  return: '退回修改',
  cancel: '撤销',
  cancel_request: '申请撤销',
  cancel_approve: '撤销通过',
  cancel_reject: '撤销驳回',
  copy: '复制创建',
};

/** 附件类型（PRD-05 §5.2 attachment_type） */
export const APPLICATION_ATTACHMENT_TYPE = {
  MEETING_NOTICE: 1,
  BUSINESS_TRIP: 2,
  OTHER: 99,
} as const;

export const APPLICATION_ATTACHMENT_TYPE_OPTIONS = [
  { value: APPLICATION_ATTACHMENT_TYPE.MEETING_NOTICE, label: '会议通知' },
  { value: APPLICATION_ATTACHMENT_TYPE.BUSINESS_TRIP, label: '出差审批' },
  { value: APPLICATION_ATTACHMENT_TYPE.OTHER, label: '其他' },
] as const;

/** 申请附件上限（D-73：单笔 5 个 / 单文件 10MB / 白名单扩展） */
export const APPLICATION_ATTACHMENT_MAX_COUNT = 5;
export const APPLICATION_ATTACHMENT_MAX_SIZE = 10 * 1024 * 1024;
export const APPLICATION_ATTACHMENT_ALLOWED_EXT = '.pdf,.jpg,.jpeg,.png';

/** 审批任务状态（PRD-05 §5.3） */
export const APPROVAL_TASK_STATUS = {
  PENDING: 1,
  APPROVED: 2,
  REJECTED: 3,
  RETURNED: 4,
  CANCELLED: 5,
} as const;

export const APPROVAL_TASK_STATUS_OPTIONS = [
  { value: APPROVAL_TASK_STATUS.PENDING, label: '待处理' },
  { value: APPROVAL_TASK_STATUS.APPROVED, label: '通过' },
  { value: APPROVAL_TASK_STATUS.REJECTED, label: '驳回' },
  { value: APPROVAL_TASK_STATUS.RETURNED, label: '退回' },
  { value: APPROVAL_TASK_STATUS.CANCELLED, label: '已取消' },
] as const;

/** 审批任务类型（task_type） */
export const APPROVAL_TASK_TYPE = {
  APPLICATION: 1,
  CANCEL_CONFIRM: 2,
} as const;

/** 角色码（与 backend RolesSeeder 对齐，用于前端 Tab/按钮可见性判断） */
export const ROLE_CODE = {
  SUPER_ADMIN: 'super_admin',
  SYSTEM_ADMIN: 'system_admin',
  DISPATCHER: 'dispatcher',
  APPROVER: 'approver',
  APPLICANT: 'applicant',
  DRIVER: 'driver_role',
} as const;

// ============================================================
// 派车单（PRD-06，与 backend DispatchService / DispatchResource 对齐）
// ============================================================

/** 派车单状态（PRD-06 §4.2，5 个值） */
export const DISPATCH_STATUS = {
  DISPATCHED: 1,
  RUNNING: 2,
  COMPLETED: 3,
  CANCELLED: 4,
  ABNORMAL: 5,
} as const;

export const DISPATCH_STATUS_OPTIONS = [
  { value: DISPATCH_STATUS.DISPATCHED, label: '已派车' },
  { value: DISPATCH_STATUS.RUNNING, label: '行驶中' },
  { value: DISPATCH_STATUS.COMPLETED, label: '已完成' },
  { value: DISPATCH_STATUS.CANCELLED, label: '已取消' },
  { value: DISPATCH_STATUS.ABNORMAL, label: '异常终止' },
] as const;

/** 状态 → Antd Tag 色（已派车蓝/行驶中青/完成绿/取消灰/异常红） */
export const DISPATCH_STATUS_COLOR: Record<number, string> = {
  1: 'processing',
  2: 'cyan',
  3: 'success',
  4: 'default',
  5: 'error',
};

/** 进行中状态集合（用于"进行中" Tab 筛选） */
export const DISPATCH_OPEN_STATUSES = [
  DISPATCH_STATUS.DISPATCHED,
  DISPATCH_STATUS.RUNNING,
] as const;

/** 司机确认状态（D-86 软性确认，不阻塞调度状态机） */
export const DRIVER_CONFIRM_STATUS = {
  PENDING: 0,
  ACCEPTED: 1,
  REJECTED: 2,
} as const;

export const DRIVER_CONFIRM_STATUS_OPTIONS = [
  { value: DRIVER_CONFIRM_STATUS.PENDING, label: '未响应' },
  { value: DRIVER_CONFIRM_STATUS.ACCEPTED, label: '已接受' },
  { value: DRIVER_CONFIRM_STATUS.REJECTED, label: '已拒绝' },
] as const;

export const DRIVER_CONFIRM_STATUS_COLOR: Record<number, string> = {
  0: 'default',
  1: 'success',
  2: 'error',
};

/** 异常终止级别 */
export const TERMINATE_LEVEL = {
  MINOR: 1,
  NORMAL: 2,
  SEVERE: 3,
} as const;

export const TERMINATE_LEVEL_OPTIONS = [
  { value: TERMINATE_LEVEL.MINOR, label: '轻微' },
  { value: TERMINATE_LEVEL.NORMAL, label: '一般' },
  { value: TERMINATE_LEVEL.SEVERE, label: '严重' },
] as const;

export const TERMINATE_LEVEL_COLOR: Record<number, string> = {
  1: 'warning',
  2: 'orange',
  3: 'error',
};

/** 派车日志动作（与 DispatchLogResource::actionLabel 对齐） */
export const DISPATCH_LOG_ACTION_LABEL: Record<string, string> = {
  create: '创建派车',
  reassign: '改派',
  depart: '出发',
  complete: '完成',
  cancel: '取消',
  terminate: '异常终止',
  driver_accept: '司机接单',
  driver_reject: '司机拒单',
};

/** 派车日志动作 → Antd Timeline.Item color（绿=正向推进，红=终止类，灰=信息类） */
export const DISPATCH_LOG_ACTION_COLOR: Record<string, string> = {
  create: 'blue',
  reassign: 'orange',
  depart: 'cyan',
  complete: 'green',
  cancel: 'gray',
  terminate: 'red',
  driver_accept: 'green',
  driver_reject: 'red',
};

// ============================================================
// 操作日志（PRD-07，对齐 IndexOperationLogRequest::ALLOWED_MODULES / ALLOWED_ACTIONS）
// ============================================================

export const OPERATION_LOG_MODULE_OPTIONS = [
  { value: 'user', label: '用户' },
  { value: 'department', label: '部门' },
  { value: 'vehicle', label: '车辆' },
  { value: 'driver', label: '司机' },
  { value: 'application', label: '用车申请' },
  { value: 'dispatch', label: '派车单' },
  { value: 'role', label: '角色' },
  { value: 'permission', label: '权限' },
  { value: 'system', label: '系统' },
  { value: 'auth', label: '认证' },
] as const;

export const OPERATION_LOG_ACTION_OPTIONS = [
  { value: 'create', label: '创建' },
  { value: 'update', label: '修改' },
  { value: 'delete', label: '删除' },
  { value: 'status_change', label: '状态变更' },
  { value: 'approve', label: '审批通过' },
  { value: 'reject', label: '驳回' },
  { value: 'cancel', label: '撤销' },
  { value: 'import', label: '导入' },
  { value: 'export', label: '导出' },
  { value: 'login', label: '登录' },
  { value: 'depart', label: '出发' },
  { value: 'complete', label: '完成' },
  { value: 'terminate', label: '异常终止' },
  { value: 'driver_accept', label: '司机接单' },
  { value: 'driver_reject', label: '司机拒单' },
  { value: 'return', label: '退回' },
  { value: 'reassign', label: '改派' },
] as const;

/** 操作日志模块 → 中文 label 映射 */
export const OPERATION_LOG_MODULE_LABEL: Record<string, string> = Object.fromEntries(
  OPERATION_LOG_MODULE_OPTIONS.map((o) => [o.value, o.label]),
);

/** 操作日志动作 → 中文 label 映射 */
export const OPERATION_LOG_ACTION_LABEL: Record<string, string> = Object.fromEntries(
  OPERATION_LOG_ACTION_OPTIONS.map((o) => [o.value, o.label]),
);

// ============================================================
// 登录日志（PRD-07 §7.4）
// ============================================================

export const LOGIN_METHOD_OPTIONS = [
  { value: 'password', label: '密码' },
  { value: 'sms', label: '短信' },
] as const;

export const LOGIN_METHOD_LABEL: Record<string, string> = Object.fromEntries(
  LOGIN_METHOD_OPTIONS.map((o) => [o.value, o.label]),
);

// ============================================================
// M8 多级审批流 — 流程定义（PRD-08 §6.1，对齐 backend migrations 2026_05_20_100001/2）
// ============================================================

/** 流程定义状态（flow_definitions.status） */
export const FLOW_DEFINITION_STATUS = {
  ACTIVE: 1,
  INACTIVE: 2,
  ARCHIVED: 3,
} as const;

export const FLOW_DEFINITION_STATUS_OPTIONS = [
  { value: FLOW_DEFINITION_STATUS.ACTIVE, label: '启用' },
  { value: FLOW_DEFINITION_STATUS.INACTIVE, label: '停用' },
  { value: FLOW_DEFINITION_STATUS.ARCHIVED, label: '归档' },
] as const;

export const FLOW_DEFINITION_STATUS_COLOR: Record<number, string> = {
  1: 'success',
  2: 'default',
  3: 'error',
};

/** 流程定义版本状态（flow_definition_versions.status） */
export const FLOW_DEFINITION_VERSION_STATUS = {
  DRAFT: 1,
  PUBLISHED: 2,
  ARCHIVED: 3,
} as const;

export const FLOW_DEFINITION_VERSION_STATUS_OPTIONS = [
  { value: FLOW_DEFINITION_VERSION_STATUS.DRAFT, label: '草稿' },
  { value: FLOW_DEFINITION_VERSION_STATUS.PUBLISHED, label: '已发布' },
  { value: FLOW_DEFINITION_VERSION_STATUS.ARCHIVED, label: '已归档' },
] as const;

export const FLOW_DEFINITION_VERSION_STATUS_COLOR: Record<number, string> = {
  1: 'default',
  2: 'success',
  3: 'error',
};

/** flow_key 校验正则（镜像 CreateFlowDefinitionRequest::rules） */
export const FLOW_KEY_REGEX = /^[a-z][a-z0-9_]{1,62}$/;

/** BPMN XML 最大字节数（CreateFlowDefinitionRequest::rules `max:2097152`） */
export const FLOW_BPMN_MAX_SIZE = 2 * 1024 * 1024;

/** 归档原因最大字符数（ArchiveFlowDefinitionRequest::rules `max:256`） */
export const FLOW_ARCHIVE_REASON_MAX = 256;

// ============================================================
// M8 流程实例 & 节点操作（PRD-08 §6.2 §6.3，对齐 backend FlowInstance/FlowNode）
// ============================================================

export const ROUTE_FLOW_INSTANCES = '/flow/instances';
export const ROUTE_FLOW_HOLIDAYS = '/flow/holidays';
export const ROUTE_FLOW_QUOTA = '/flow/quota';
export const ROUTE_MY_TODO = '/my-todo';

// ============================================================
// M10 切片④ driver_role 自助入口（我的工作台）
// ============================================================

export const ROUTE_MY_SCHEDULE = '/my-schedule';
export const ROUTE_MY_PERFORMANCE = '/my-performance';

/** 流程实例状态（PRD-08 §4.4 表 1~8） */
export const FLOW_INSTANCE_STATUS = {
  RUNNING: 1,
  RUNNING_TIMEOUT: 2,
  APPROVED: 3,
  REJECTED: 4,
  TERMINATED: 5,
  STEP_OVERFLOW_TERMINATED: 6,
  REVOKED: 7,
  URGENT_DISPATCHED_PENDING_POST_APPROVAL: 8,
} as const;

export const FLOW_INSTANCE_STATUS_OPTIONS = [
  { value: FLOW_INSTANCE_STATUS.RUNNING, label: '进行中' },
  { value: FLOW_INSTANCE_STATUS.RUNNING_TIMEOUT, label: '进行中(已超时)' },
  { value: FLOW_INSTANCE_STATUS.APPROVED, label: '已通过' },
  { value: FLOW_INSTANCE_STATUS.REJECTED, label: '已驳回' },
  { value: FLOW_INSTANCE_STATUS.TERMINATED, label: '已终止' },
  { value: FLOW_INSTANCE_STATUS.STEP_OVERFLOW_TERMINATED, label: '已超时终止' },
  { value: FLOW_INSTANCE_STATUS.REVOKED, label: '已撤销' },
  { value: FLOW_INSTANCE_STATUS.URGENT_DISPATCHED_PENDING_POST_APPROVAL, label: '紧急待追认' },
] as const;

/** 实例终态判定（用于操作可见性：仅非终态可强制终止） */
export const FLOW_INSTANCE_TERMINAL_STATUSES: number[] = [
  FLOW_INSTANCE_STATUS.APPROVED,
  FLOW_INSTANCE_STATUS.REJECTED,
  FLOW_INSTANCE_STATUS.TERMINATED,
  FLOW_INSTANCE_STATUS.STEP_OVERFLOW_TERMINATED,
  FLOW_INSTANCE_STATUS.REVOKED,
];

/** 节点运行时状态（PRD-08 §4.4 表 1~7） */
export const FLOW_NODE_RUNTIME_STATUS = {
  PENDING: 1,
  APPROVED: 2,
  REJECTED: 3,
  RETURNED: 4,
  REMINDED: 5,
  AUTO_APPROVED: 6,
  TIMEOUT_REJECTED: 7,
} as const;

export const FLOW_NODE_RUNTIME_STATUS_OPTIONS = [
  { value: FLOW_NODE_RUNTIME_STATUS.PENDING, label: '待处理' },
  { value: FLOW_NODE_RUNTIME_STATUS.APPROVED, label: '已通过' },
  { value: FLOW_NODE_RUNTIME_STATUS.REJECTED, label: '已驳回' },
  { value: FLOW_NODE_RUNTIME_STATUS.RETURNED, label: '已退回' },
  { value: FLOW_NODE_RUNTIME_STATUS.REMINDED, label: '已提醒' },
  { value: FLOW_NODE_RUNTIME_STATUS.AUTO_APPROVED, label: '已自动通过' },
  { value: FLOW_NODE_RUNTIME_STATUS.TIMEOUT_REJECTED, label: '已超时驳回' },
] as const;

/** 节点意见与终止原因最大字符数（镜像后端 Form Request `max:256`） */
export const FLOW_NODE_ACTION_COMMENT_MAX = 256;
export const FLOW_INSTANCE_TERMINATED_REASON_MAX = 256;

/** 节假日 day_type（PRD-08 §5.6） */
export const HOLIDAY_DAY_TYPE = {
  LEGAL_HOLIDAY: 1,
  COMPENSATORY_WORKDAY: 2,
  COMPENSATORY_HOLIDAY: 3,
  TEMPORARY_HOLIDAY: 4,
} as const;

export const HOLIDAY_DAY_TYPE_OPTIONS = [
  { value: HOLIDAY_DAY_TYPE.LEGAL_HOLIDAY, label: '法定节假日' },
  { value: HOLIDAY_DAY_TYPE.COMPENSATORY_WORKDAY, label: '调休工作日' },
  { value: HOLIDAY_DAY_TYPE.COMPENSATORY_HOLIDAY, label: '调休假日' },
  { value: HOLIDAY_DAY_TYPE.TEMPORARY_HOLIDAY, label: '临时假日' },
] as const;

export const HOLIDAY_DAY_TYPE_COLOR: Record<number, string> = {
  1: 'red',
  2: 'orange',
  3: 'volcano',
  4: 'purple',
};

/** 节假日来源（PRD-08 §5.6 source） */
export const HOLIDAY_SOURCE = {
  THIRD_PARTY: 1,
  MANUAL: 2,
  MANUAL_OVERRIDE: 3,
} as const;

export const HOLIDAY_SOURCE_LABEL: Record<number, string> = {
  1: '第三方拉取',
  2: '后台手动',
  3: '手动覆盖第三方',
};

/** 手动覆盖原因最大字符数（镜像 UpsertHolidayRequest::rules） */
export const HOLIDAY_OVERRIDE_REASON_MAX = 256;

// ============================================================
// M9 车辆维护（PRD-09，对齐 backend MaintenanceOrderService / Resource）
// ============================================================

/** 维护工单状态（PRD-09 §5.1） */
export const MAINTENANCE_ORDER_STATUS = {
  PENDING: 1,
  IN_PROGRESS: 2,
  COMPLETED: 3,
  CANCELLED: 4,
} as const;

export const MAINTENANCE_ORDER_STATUS_OPTIONS = [
  { value: MAINTENANCE_ORDER_STATUS.PENDING, label: '待处理' },
  { value: MAINTENANCE_ORDER_STATUS.IN_PROGRESS, label: '进行中' },
  { value: MAINTENANCE_ORDER_STATUS.COMPLETED, label: '已完成' },
  { value: MAINTENANCE_ORDER_STATUS.CANCELLED, label: '已取消' },
] as const;

/** 状态 → Antd Tag 色（PRD-09 §7.1：灰=待处理 / 蓝=进行中 / 绿=已完成 / 红=已取消） */
export const MAINTENANCE_ORDER_STATUS_COLOR: Record<number, string> = {
  1: 'default',
  2: 'processing',
  3: 'success',
  4: 'error',
};

/** 维护工单类型 */
export const MAINTENANCE_ORDER_TYPE = {
  REPAIR: 1,
  MAINTENANCE: 2,
  INSPECTION: 3,
  INSURANCE: 4,
} as const;

export const MAINTENANCE_ORDER_TYPE_OPTIONS = [
  { value: MAINTENANCE_ORDER_TYPE.REPAIR, label: '维修' },
  { value: MAINTENANCE_ORDER_TYPE.MAINTENANCE, label: '保养' },
  { value: MAINTENANCE_ORDER_TYPE.INSPECTION, label: '年检' },
  { value: MAINTENANCE_ORDER_TYPE.INSURANCE, label: '保险续保' },
] as const;

/** 需要新到期日的工单类型（AC-06：32000050） */
export const MAINTENANCE_TYPES_NEEDING_EXPIRY = [
  MAINTENANCE_ORDER_TYPE.INSPECTION,
  MAINTENANCE_ORDER_TYPE.INSURANCE,
] as const;

/** 优先级 */
export const MAINTENANCE_ORDER_PRIORITY = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
} as const;

export const MAINTENANCE_ORDER_PRIORITY_OPTIONS = [
  { value: MAINTENANCE_ORDER_PRIORITY.LOW, label: '低' },
  { value: MAINTENANCE_ORDER_PRIORITY.MEDIUM, label: '中' },
  { value: MAINTENANCE_ORDER_PRIORITY.HIGH, label: '高' },
] as const;

export const MAINTENANCE_ORDER_PRIORITY_COLOR: Record<number, string> = {
  1: 'default',
  2: 'processing',
  3: 'error',
};

/** 维护工单凭证类型（PRD-09 §5.2 doc_type） */
export const MAINTENANCE_DOC_TYPE = {
  FAULT_PHOTO: 1,
  REPAIR_RECEIPT: 2,
  INSPECTION_CERT: 3,
  INSURANCE_POLICY: 4,
  MAINTENANCE_RECEIPT: 5,
  OTHER: 99,
} as const;

export const MAINTENANCE_DOC_TYPE_OPTIONS = [
  { value: MAINTENANCE_DOC_TYPE.FAULT_PHOTO, label: '故障照片' },
  { value: MAINTENANCE_DOC_TYPE.REPAIR_RECEIPT, label: '维修凭证' },
  { value: MAINTENANCE_DOC_TYPE.INSPECTION_CERT, label: '年检证明' },
  { value: MAINTENANCE_DOC_TYPE.INSURANCE_POLICY, label: '保险单' },
  { value: MAINTENANCE_DOC_TYPE.MAINTENANCE_RECEIPT, label: '保养凭证' },
  { value: MAINTENANCE_DOC_TYPE.OTHER, label: '其他' },
] as const;

/** 车辆维护阻塞类型（PRD-09 §5.3） */
export const MAINTENANCE_BLOCK_TYPE = {
  REPAIR: 1,
  MAINTENANCE: 2,
  INSPECTION: 3,
  INSURANCE: 4,
  FAULT: 5,
} as const;

export const MAINTENANCE_BLOCK_TYPE_OPTIONS = [
  { value: MAINTENANCE_BLOCK_TYPE.REPAIR, label: '维修中' },
  { value: MAINTENANCE_BLOCK_TYPE.MAINTENANCE, label: '保养中' },
  { value: MAINTENANCE_BLOCK_TYPE.INSPECTION, label: '待年检' },
  { value: MAINTENANCE_BLOCK_TYPE.INSURANCE, label: '待保险' },
  { value: MAINTENANCE_BLOCK_TYPE.FAULT, label: '故障' },
] as const;

export const MAINTENANCE_BLOCK_TYPE_LABEL: Record<number, string> = Object.fromEntries(
  MAINTENANCE_BLOCK_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

// ============================================================
// M9 维护规则（PRD-09 §6.4 + §5.4 maintenance_rules）
// ============================================================

/** 规则类型（PRD-09 §5.4 rule_type） */
export const MAINTENANCE_RULE_TYPE = {
  INSURANCE: 1,
  INSPECTION: 2,
  MAINTENANCE_BY_MILEAGE: 3,
  MAINTENANCE_BY_TIME: 4,
  QUALIFICATION: 5,
} as const;

export const MAINTENANCE_RULE_TYPE_OPTIONS = [
  { value: MAINTENANCE_RULE_TYPE.INSURANCE, label: '保险提醒' },
  { value: MAINTENANCE_RULE_TYPE.INSPECTION, label: '年检提醒' },
  { value: MAINTENANCE_RULE_TYPE.MAINTENANCE_BY_MILEAGE, label: '保养里程' },
  { value: MAINTENANCE_RULE_TYPE.MAINTENANCE_BY_TIME, label: '保养时间' },
  { value: MAINTENANCE_RULE_TYPE.QUALIFICATION, label: '资质提醒' },
] as const;

export const MAINTENANCE_RULE_TYPE_LABEL: Record<number, string> = Object.fromEntries(
  MAINTENANCE_RULE_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

/** interval_unit（仅保养类规则使用） */
export const MAINTENANCE_INTERVAL_UNIT = {
  KM: 1,
  MONTH: 2,
} as const;

export const MAINTENANCE_INTERVAL_UNIT_OPTIONS = [
  { value: MAINTENANCE_INTERVAL_UNIT.KM, label: 'km' },
  { value: MAINTENANCE_INTERVAL_UNIT.MONTH, label: '月' },
] as const;

/** 通知角色 code 选项（PRD-09 §5.4 notify_role_codes JSON 数组） */
export const NOTIFY_ROLE_CODE_OPTIONS = [
  { value: 'system_admin', label: '系统管理员' },
  { value: 'dispatcher', label: '调度员' },
  { value: 'approver', label: '审批人' },
  { value: 'driver_role', label: '司机' },
] as const;

export const MAINTENANCE_RULE_ADVANCE_DAYS_MAX = 365;
export const MAINTENANCE_RULE_NAME_MAX = 64;

// ============================================================
// M9 字典管理（PRD-09 §6.7 / D-48）
// ============================================================

/** dict_code 取值（D-48 一期内置） */
export const DICTIONARY_CODE = {
  VEHICLE_TYPE: 'vehicle_type',
  FUEL_TYPE: 'fuel_type',
} as const;

export const DICTIONARY_CODE_MAX = 32;
export const DICTIONARY_NAME_MAX = 64;
export const DICTIONARY_ITEM_CODE_MAX = 32;
export const DICTIONARY_ITEM_NAME_MAX = 64;

// ============================================================
// M9 故障上报（PRD-09 §6.3）
// ============================================================

/** 故障严重等级（PRD-09 §5.6 severity） */
export const FAULT_SEVERITY = {
  MINOR: 1,
  SEVERE: 2,
  URGENT: 3,
} as const;

export const FAULT_SEVERITY_OPTIONS = [
  { value: FAULT_SEVERITY.MINOR, label: '一般' },
  { value: FAULT_SEVERITY.SEVERE, label: '严重' },
  { value: FAULT_SEVERITY.URGENT, label: '紧急' },
] as const;

export const FAULT_SEVERITY_LABEL: Record<number, string> = Object.fromEntries(
  FAULT_SEVERITY_OPTIONS.map((o) => [o.value, o.label]),
);

export const FAULT_SEVERITY_COLOR: Record<number, string> = {
  1: 'default',
  2: 'warning',
  3: 'error',
};

/** 上报人类型（PRD-09 §5.6 reporter_type） */
export const FAULT_REPORTER_TYPE_LABEL: Record<number, string> = {
  1: '司机',
  2: '调度员',
  3: '管理员',
};

/** 故障状态（PRD-09 §5.6 status） */
export const FAULT_STATUS_LABEL: Record<number, string> = {
  1: '待处理',
  2: '已转工单',
  3: '已忽略',
};

export const FAULT_STATUS_COLOR: Record<number, string> = {
  1: 'processing',
  2: 'success',
  3: 'default',
};

// ============================================================
// M9 到期提醒（PRD-09 §6.5）
// ============================================================

/** 提醒类型（PRD-09 §5.5 reminder_type） */
export const REMINDER_TYPE = {
  INSURANCE: 1,
  INSPECTION: 2,
  MAINTENANCE: 3,
  DRIVER_LICENSE: 4,
  DRIVER_QUALIFICATION: 5,
} as const;

export const REMINDER_TYPE_OPTIONS = [
  { value: REMINDER_TYPE.INSURANCE, label: '保险到期' },
  { value: REMINDER_TYPE.INSPECTION, label: '年检到期' },
  { value: REMINDER_TYPE.MAINTENANCE, label: '保养到期' },
  { value: REMINDER_TYPE.DRIVER_LICENSE, label: '驾驶证到期' },
  { value: REMINDER_TYPE.DRIVER_QUALIFICATION, label: '从业资格证到期' },
] as const;

export const REMINDER_TYPE_LABEL: Record<number, string> = Object.fromEntries(
  REMINDER_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

export const REMINDER_TYPE_COLOR: Record<number, string> = {
  1: 'blue',
  2: 'gold',
  3: 'green',
  4: 'purple',
  5: 'magenta',
};

/** 目标类型（PRD-09 §5.5 target_type） */
export const REMINDER_TARGET_TYPE = {
  VEHICLE: 1,
  DRIVER: 2,
} as const;

export const REMINDER_TARGET_TYPE_OPTIONS = [
  { value: REMINDER_TARGET_TYPE.VEHICLE, label: '车辆' },
  { value: REMINDER_TARGET_TYPE.DRIVER, label: '司机' },
] as const;

export const REMINDER_TARGET_TYPE_LABEL: Record<number, string> = Object.fromEntries(
  REMINDER_TARGET_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

/** 提醒状态（PRD-09 §5.5 status） */
export const REMINDER_STATUS = {
  SENT: 1,
  FAILED: 2,
  CONFIRMED: 3,
  IGNORED: 4,
} as const;

export const REMINDER_STATUS_OPTIONS = [
  { value: REMINDER_STATUS.SENT, label: '已发送' },
  { value: REMINDER_STATUS.FAILED, label: '发送失败' },
  { value: REMINDER_STATUS.CONFIRMED, label: '已确认' },
  { value: REMINDER_STATUS.IGNORED, label: '已忽略' },
] as const;

export const REMINDER_STATUS_LABEL: Record<number, string> = Object.fromEntries(
  REMINDER_STATUS_OPTIONS.map((o) => [o.value, o.label]),
);

export const REMINDER_STATUS_COLOR: Record<number, string> = {
  1: 'processing',
  2: 'error',
  3: 'success',
  4: 'default',
};

/** 通知通道（PRD-09 §5.5 channel） */
export const REMINDER_CHANNEL_LABEL: Record<number, string> = {
  1: '站内',
  2: '短信',
  3: '邮件',
};

/** 距到期天数色标（PRD-09 §7.6）：<0 红 / ≤7 黄 / ≤30 蓝 / else 绿 */
export const reminderDaysColor = (days: number): string => {
  if (days < 0) return 'error';
  if (days <= 7) return 'warning';
  if (days <= 30) return 'processing';
  return 'success';
};

// ============================================================
// M10 司机排班与考核 (PRD-10 §4 + §7)
// ============================================================

/** 排班状态值（PRD-10 §4.2） */
export const SCHEDULE_STATUS = {
  PENDING: 1,
  RUNNING: 2,
  COMPLETED: 3,
  CANCELLED: 4,
} as const;

export const SCHEDULE_STATUS_OPTIONS = [
  { value: SCHEDULE_STATUS.PENDING, label: '待执行' },
  { value: SCHEDULE_STATUS.RUNNING, label: '执行中' },
  { value: SCHEDULE_STATUS.COMPLETED, label: '已完成' },
  { value: SCHEDULE_STATUS.CANCELLED, label: '已取消' },
] as const;

export const SCHEDULE_STATUS_COLOR: Record<number, string> = {
  1: 'default',
  2: 'processing',
  3: 'success',
  4: 'error',
};

export const SCHEDULE_STATUS_LABEL: Record<number, string> = {
  1: '待执行',
  2: '执行中',
  3: '已完成',
  4: '已取消',
};

/** 考核等级色标（PRD-10 §7.10 #8）：A=绿 / B=蓝 / C=黄 / D=红 */
export const EVAL_GRADE_COLOR: Record<string, string> = {
  A: 'success',
  B: 'processing',
  C: 'warning',
  D: 'error',
};

export const EVAL_GRADE_OPTIONS = [
  { value: 'A', label: 'A（优秀，≥90）' },
  { value: 'B', label: 'B（良好，80~89）' },
  { value: 'C', label: 'C（合格，60~79）' },
  { value: 'D', label: 'D（不合格，<60）' },
] as const;

// ============================================================
// M10 切片② 考核指标 / 考核记录 / 奖惩(PRD-10 §5.4~§5.8 + §7.4~§7.7)
// ============================================================

/** 考核指标分类(PRD-10 §5.4) */
export const INDICATOR_CATEGORY = {
  SAFETY: 1,
  SERVICE: 2,
  EFFICIENCY: 3,
  COMPLIANCE: 4,
} as const;

export const INDICATOR_CATEGORY_OPTIONS = [
  { value: 1, label: '安全' },
  { value: 2, label: '服务' },
  { value: 3, label: '效率' },
  { value: 4, label: '合规' },
] as const;

export const INDICATOR_CATEGORY_COLOR: Record<number, string> = {
  1: 'error',
  2: 'processing',
  3: 'gold',
  4: 'purple',
};

/** 考核周期类型(PRD-10 §5.5) */
export const PERF_PERIOD_TYPE_OPTIONS = [
  { value: 1, label: '月' },
  { value: 2, label: '季' },
  { value: 3, label: '年' },
] as const;

/** 考核记录状态(PRD-10 §4.5) */
export const PERF_STATUS = {
  DRAFT: 1,
  SUBMITTED: 2,
  CONFIRMED: 3,
  APPEALED: 4,
  REVISED: 5,
} as const;

export const PERF_STATUS_OPTIONS = [
  { value: PERF_STATUS.DRAFT, label: '草稿' },
  { value: PERF_STATUS.SUBMITTED, label: '已提交' },
  { value: PERF_STATUS.CONFIRMED, label: '已确认' },
  { value: PERF_STATUS.APPEALED, label: '已申诉' },
  { value: PERF_STATUS.REVISED, label: '已修正' },
] as const;

export const PERF_STATUS_COLOR: Record<number, string> = {
  1: 'default',
  2: 'processing',
  3: 'success',
  4: 'warning',
  5: 'purple',
};

export const PERF_STATUS_LABEL: Record<number, string> = {
  1: '草稿',
  2: '已提交',
  3: '已确认',
  4: '已申诉',
  5: '已修正',
};

/** 考核附件类型(PRD-10 §5.7) */
export const PERF_DOC_TYPE_OPTIONS = [
  { value: 1, label: '考核证明' },
  { value: 2, label: '申诉材料' },
  { value: 3, label: '修正材料' },
  { value: 99, label: '其他' },
] as const;

export const PERF_DOC_TYPE_LABEL: Record<number, string> = {
  1: '考核证明',
  2: '申诉材料',
  3: '修正材料',
  99: '其他',
};

/** 奖惩类型(PRD-10 §5.8) */
export const REWARD_PENALTY_TYPE = {
  REWARD: 1,
  PENALTY: 2,
} as const;

export const REWARD_PENALTY_TYPE_OPTIONS = [
  { value: 1, label: '奖励' },
  { value: 2, label: '惩罚' },
] as const;

export const REWARD_PENALTY_TYPE_COLOR: Record<number, string> = {
  1: 'success',
  2: 'error',
};

/** 奖惩类别(同考核指标 4 分类) */
export const REWARD_PENALTY_CATEGORY_OPTIONS = INDICATOR_CATEGORY_OPTIONS;
export const REWARD_PENALTY_CATEGORY_COLOR = INDICATOR_CATEGORY_COLOR;

// ============================================================
// M11 通知模块（PRD-11 §5.6 §5.7）
// ============================================================

/** 发送日志状态展示（与 backend NotificationLog::STATUS_* 一致） */
export const NOTIFICATION_LOG_STATUS_OPTIONS = [
  { value: 'pending', label: '待发' },
  { value: 'sent', label: '已发' },
  { value: 'failed', label: '失败' },
  { value: 'degraded', label: '降级' },
  { value: 'suspended', label: '暂停' },
] as const;

export const NOTIFICATION_LOG_STATUS_COLOR: Record<string, string> = {
  pending: 'processing',
  sent: 'success',
  failed: 'error',
  degraded: 'warning',
  suspended: 'default',
};

/** 通道选项（与 backend NotificationChannel 一致） */
export const NOTIFICATION_CHANNEL_OPTIONS = [
  { value: 'inbox', label: '站内消息' },
  { value: 'email', label: '邮件' },
  { value: 'sms', label: '短信' },
  { value: 'wechat', label: '微信' },
  { value: 'webhook', label: 'Webhook' },
] as const;

export const NOTIFICATION_CHANNEL_COLOR: Record<string, string> = {
  inbox: 'blue',
  email: 'purple',
  sms: 'cyan',
  wechat: 'green',
  webhook: 'gold',
};

/** SMTP 加密方式 */
export const SMTP_ENCRYPTION_OPTIONS = [
  { value: 'tls', label: 'STARTTLS（推荐 587）' },
  { value: 'ssl', label: 'SSL/TLS（465）' },
  { value: 'none', label: '不加密（不推荐）' },
] as const;

/** 批量重发上限（与 backend NotificationLogService 一致；超出抛 60060030） */
export const NOTIFICATION_LOG_BATCH_RESEND_MAX = 50;

/** 站内消息分类（backend NotificationInboxResource::category_label 对应） */
export const NOTIFICATION_CATEGORY_OPTIONS = [
  { value: 1, label: '申请' },
  { value: 2, label: '审批' },
  { value: 3, label: '派车' },
  { value: 4, label: '维护' },
  { value: 5, label: '排班' },
  { value: 6, label: '系统' },
] as const;

export const NOTIFICATION_CATEGORY_COLOR: Record<number, string> = {
  1: 'blue',
  2: 'purple',
  3: 'cyan',
  4: 'orange',
  5: 'green',
  6: 'default',
};

/** 黑名单 source（与 backend NotificationBlacklist::SOURCE_* 一致） */
export const BLACKLIST_SOURCE = {
  USER_UNSUBSCRIBE: 1,
  CHANNEL_BOUNCE: 2,
  ADMIN_MANUAL: 3,
} as const;

export const BLACKLIST_SOURCE_OPTIONS = [
  { value: 1, label: '用户退订', color: 'orange' },
  { value: 2, label: '通道退信', color: 'red' },
  { value: 3, label: '手工添加', color: 'blue' },
] as const;

/** 偏好/事件通道（与 backend NotificationChannel 字符串一致） */
export const NOTIFICATION_PREFERENCE_CHANNELS = [
  { value: 'in_app', label: '站内' },
  { value: 'sms', label: '短信' },
  { value: 'email', label: '邮件' },
] as const;

/** 事件优先级（与 backend NotificationEvent::PRIORITY_* 一致） */
export const NOTIFICATION_PRIORITY_OPTIONS = [
  { value: 1, label: '紧急', color: 'red' },
  { value: 2, label: '普通', color: 'blue' },
  { value: 3, label: '低', color: 'default' },
] as const;

/** 偏好来源 */
export const PREFERENCE_SOURCE = {
  USER_SELF: 1,
  ADMIN: 2,
} as const;

/** 预览收件人 target_type 枚举（与 PreviewRecipientsRequest 一致） */
export const PREVIEW_TARGET_TYPE_OPTIONS = [
  { value: 'application', label: '用车申请' },
  { value: 'dispatch', label: '派车单' },
  { value: 'department', label: '部门' },
  { value: 'user', label: '用户' },
] as const;

// ============================================================
// M12 费用与额度管控（PRD-12，对齐 backend QuotaPoolResource / QuotaLedgerResource）
// ============================================================

export const ROUTE_QUOTA_POOLS = '/quota-pools';
export const ROUTE_COST_RECORDS = '/cost-records';

/** 额度池状态（PRD-12 §4.4） */
export const QUOTA_POOL_STATUS = {
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  CLOSED: 'closed',
} as const;

export const QUOTA_POOL_STATUS_OPTIONS = [
  { value: QUOTA_POOL_STATUS.ACTIVE, label: '运行中' },
  { value: QUOTA_POOL_STATUS.SUSPENDED, label: '已冻结' },
  { value: QUOTA_POOL_STATUS.CLOSED, label: '已关闭' },
] as const;

export const QUOTA_POOL_STATUS_COLOR: Record<string, string> = {
  active: 'success',
  suspended: 'warning',
  closed: 'default',
};

/** 周期类型（PRD-12 §3.1） */
export const QUOTA_PERIOD_TYPE_OPTIONS = [
  { value: 'month', label: '月度' },
  { value: 'quarter', label: '季度' },
] as const;

/** 配额类型（PRD-12 §3.1） */
export const QUOTA_TYPE_OPTIONS = [
  { value: 'amount', label: '金额制' },
  { value: 'count', label: '次数制' },
] as const;

/** 结转策略（PRD-12 §3.1） */
export const CARRYOVER_POLICY_OPTIONS = [
  { value: 'clear', label: '清零' },
  { value: 'full', label: '全额结转' },
  { value: 'partial', label: '按比例结转' },
] as const;

/** 账本 action（PRD-12 §5.2） */
export const QUOTA_LEDGER_ACTION_OPTIONS = [
  { value: 'initialize', label: '初始化' },
  { value: 'reserve', label: '预占' },
  { value: 'confirm', label: '确认' },
  { value: 'settle', label: '结算' },
  { value: 'release', label: '释放' },
  { value: 'carryover', label: '结转' },
  { value: 'expire', label: '过期' },
  { value: 'adjust', label: '调账' },
] as const;

export const QUOTA_LEDGER_ACTION_COLOR: Record<string, string> = {
  initialize: 'blue',
  reserve: 'cyan',
  confirm: 'processing',
  settle: 'green',
  release: 'orange',
  carryover: 'purple',
  expire: 'default',
  adjust: 'magenta',
};

/** 告警级别（PRD-12 §3.3） */
export const QUOTA_ALERT_LEVEL_COLOR: Record<string, string> = {
  normal: 'success',
  warning: 'warning',
  critical: 'error',
  exhausted: 'error',
};

/** 费用工单状态（PRD-12 §4.2） */
export const COST_RECORD_STATUS = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  REVIEWING: 'reviewing',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ARCHIVED: 'archived',
} as const;

export const COST_RECORD_STATUS_OPTIONS = [
  { value: COST_RECORD_STATUS.DRAFT, label: '草稿' },
  { value: COST_RECORD_STATUS.SUBMITTED, label: '已提交' },
  { value: COST_RECORD_STATUS.REVIEWING, label: '复核中' },
  { value: COST_RECORD_STATUS.APPROVED, label: '已通过' },
  { value: COST_RECORD_STATUS.REJECTED, label: '已驳回' },
  { value: COST_RECORD_STATUS.ARCHIVED, label: '已归档' },
] as const;

export const COST_RECORD_STATUS_COLOR: Record<string, string> = {
  draft: 'default',
  submitted: 'processing',
  reviewing: 'warning',
  approved: 'success',
  rejected: 'error',
  archived: 'blue',
};

export const COST_RECORD_SOURCE_OPTIONS = [
  { value: 'manual', label: '手动录入' },
  { value: 'dispatch_completed', label: '派车完成' },
  { value: 'maintenance', label: '维护工单' },
] as const;

// ============================================================
// M13 报表中心（PRD-13）
// ============================================================

export const ROUTE_REPORT_CENTER = '/reports';
export const ROUTE_REPORT_DISPATCH = '/reports/dispatch';
export const ROUTE_REPORT_VEHICLE_UTILIZATION = '/reports/vehicle-utilization';
export const ROUTE_REPORT_DRIVER_WORKLOAD = '/reports/driver-workload';
export const ROUTE_REPORT_COST = '/reports/cost';
export const ROUTE_REPORT_QUOTA_USAGE = '/reports/quota-usage';
export const ROUTE_REPORT_APPROVAL_EFFICIENCY = '/reports/approval-efficiency';
export const ROUTE_REPORT_MAINTENANCE = '/reports/maintenance';
export const ROUTE_REPORT_NOTIFICATION_DELIVERY = '/reports/notification-delivery';
export const ROUTE_REPORT_EXPORTS = '/reports/exports';
export const ROUTE_REPORT_SUBSCRIPTIONS = '/reports/subscriptions';
export const ROUTE_REPORT_MONITOR = '/reports/monitor';
export const ROUTE_REPORT_DATASET = '/reports/dataset';

/** 报表编码映射 */
export const REPORT_CODE_OPTIONS = [
  { value: 'R-01', label: '派车统计' },
  { value: 'R-02', label: '车辆利用率' },
  { value: 'R-03', label: '司机工时' },
  { value: 'R-04', label: '费用统计' },
  { value: 'R-05', label: '额度使用率' },
  { value: 'R-06', label: '审批效率' },
  { value: 'R-07', label: '维护成本' },
  { value: 'R-08', label: '通知投递' },
] as const;

/** 导出任务状态 */
export const EXPORT_TASK_STATUS_OPTIONS = [
  { value: 'pending', label: '等待中' },
  { value: 'processing', label: '处理中' },
  { value: 'success', label: '已完成' },
  { value: 'failed', label: '失败' },
  { value: 'cancelled', label: '已取消' },
  { value: 'expired', label: '已过期' },
] as const;

/** 订阅频率 */
export const SUBSCRIPTION_FREQUENCY_OPTIONS = [
  { value: 'daily', label: '每日' },
  { value: 'weekly', label: '每周' },
  { value: 'monthly', label: '每月' },
] as const;

/** 订阅状态 */
export const SUBSCRIPTION_STATUS_OPTIONS = [
  { value: 'active', label: '运行中' },
  { value: 'paused', label: '已暂停' },
  { value: 'cancelled', label: '已取消' },
] as const;

// ============================================================
// P3-M3 自动调度推荐
// ============================================================

export const ROUTE_DISPATCH_RECOMMEND_CONFIG = '/dispatches/recommend-config';

export const RECOMMEND_SESSION_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
} as const;

export const RECOMMEND_SESSION_STATUS_OPTIONS = [
  { value: RECOMMEND_SESSION_STATUS.PENDING, label: '待确认' },
  { value: RECOMMEND_SESSION_STATUS.CONFIRMED, label: '已确认' },
  { value: RECOMMEND_SESSION_STATUS.REJECTED, label: '已放弃' },
  { value: RECOMMEND_SESSION_STATUS.EXPIRED, label: '已失效' },
] as const;

export const RECOMMEND_SESSION_STATUS_COLOR: Record<string, string> = {
  pending: 'processing',
  confirmed: 'success',
  rejected: 'default',
  expired: 'error',
};
